import { getConfig } from "../config";
import { getUserManager } from "../auth/userManager";
import { signInAgain } from "../auth/identityRedirect";
import { getTabSfEnv } from "../sfEnv";
import { asError, eventBus } from "@optimyl/events";
import { ApiError } from "../types";

const DEFAULT_TIMEOUT_MS = 30_000;

interface ApiFetchOptions extends RequestInit {
  timeoutMs?: number;
  /** HTTP status codes that callers handle gracefully — skip error toast for these. */
  allowedStatuses?: number[];
}

export async function apiFetch<T>(
  path: string,
  options: ApiFetchOptions = {},
): Promise<T> {
  const config = getConfig();
  const url = `${config.API_BASE_URL}${path}`;

  const { timeoutMs = DEFAULT_TIMEOUT_MS, signal: callerSignal, allowedStatuses, ...init } = options;

  // Get access token from OIDC session (skip when auth is disabled — no Identity Server)
  const authHeaders: Record<string, string> = {};
  if (config.AUTH_ENABLED) {
    const user = await getUserManager().getUser();
    if (user?.access_token) {
      authHeaders["Authorization"] = `Bearer ${user.access_token}`;
    }
  }

  // Merge caller's AbortSignal with our timeout signal
  const timeoutController = new AbortController();
  const timeoutId = setTimeout(() => timeoutController.abort(), timeoutMs);
  const signal = callerSignal
    ? AbortSignal.any([callerSignal, timeoutController.signal])
    : timeoutController.signal;

  const sfEnv = getTabSfEnv() || config.SF_ENVIRONMENT;
  const sfEnvHeader: Record<string, string> = sfEnv ? { sfEnv } : {};

  try {
    const res = await fetch(url, {
      ...init,
      signal,
      headers: {
        // Let browser set Content-Type for FormData (multipart boundary required)
        ...(init.body instanceof FormData ? {} : { "Content-Type": "application/json" }),
        ...authHeaders,
        // Salesforce environment routing — session override (env picker) takes precedence
        ...sfEnvHeader,
        ...init.headers,
      },
    });

    clearTimeout(timeoutId);

    if (res.status === 401 && config.AUTH_ENABLED) {
      // The session ended (expired, or signed out elsewhere); the caller gets this error now.
      signInAgain("apiFetch.signinRedirect");
      throw new ApiError("Session expired. Redirecting to login...", 401);
    }

    if (!res.ok) {
      // Always throw on non-2xx. Allowed statuses suppress the toast (handled
      // in the catch below) and attach the parsed body to ApiError so callers
      // can read response data without re-fetching.
      let message = `API error: ${res.status}`;
      let parsedBody: unknown;
      try {
        parsedBody = await res.json();
        const obj = parsedBody as { message?: unknown; error?: unknown };
        if (typeof obj?.message === "string") message = obj.message;
        else if (typeof obj?.error === "string") message = obj.error;
      } catch {
        // Response wasn't JSON — use status text
      }
      throw new ApiError(message, res.status, parsedBody);
    }

    // 204 No Content (or empty body) — used by DELETE and "no resource yet, no
    // payload" reads like /opportunity/{id}/latest. Caller treats undefined as null.
    if (res.status === 204 || res.headers.get("content-length") === "0") {
      return undefined as T;
    }

    return (await res.json()) as T;
  } catch (error) {
    clearTimeout(timeoutId);

    if (error instanceof ApiError) {
      // A 401 is answered by the sign-in it started, not reported.
      if (error.status !== 401 && !allowedStatuses?.includes(error.status)) {
        eventBus.publish("error", { error, context: `apiFetch ${path}` });
      }
      throw error;
    }

    const err = asError(error);
    if (err.name === "AbortError") {
      const timeoutError = new ApiError("Request timed out", 408);
      eventBus.publish("error", { error: timeoutError, context: `apiFetch ${path} timeout` });
      throw timeoutError;
    }

    eventBus.publish("error", { error: err, context: `apiFetch ${path}` });
    throw err;
  }
}
